<script setup lang="ts">
import { computed } from 'vue';
import { TrendingUp, TrendingDown, Minus } from 'lucide-vue-next';
import { cn } from '../../design-system/utils';

interface Props {
  label: string;
  value: string | number;
  hint?: string;
  delta?: number;
  deltaLabel?: string;
  deltaDirection?: 'up-good' | 'up-bad';
}

const props = withDefaults(defineProps<Props>(), {
  deltaDirection: 'up-good',
});

const deltaColorClass = computed(() => {
  if (props.delta === undefined || props.delta === 0) return 'text-text-muted';
  const isUp = props.delta > 0;
  const isGood = (isUp && props.deltaDirection === 'up-good') || (!isUp && props.deltaDirection === 'up-bad');
  return isGood ? 'text-success' : 'text-danger';
});

const DeltaIcon = computed(() => {
  if (props.delta === undefined || props.delta === 0) return Minus;
  return props.delta > 0 ? TrendingUp : TrendingDown;
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <span class="ds-stat-label text-xs font-medium uppercase tracking-wide text-text-muted">
      {{ label }}
    </span>
    <span class="ds-stat-value tabular-nums text-3xl font-semibold font-display tracking-tight text-text">
      {{ value }}
    </span>
    <div v-if="delta !== undefined || hint" class="flex items-center gap-2 text-sm">
      <span v-if="delta !== undefined" :class="cn('inline-flex items-center gap-1 font-medium tabular-nums', deltaColorClass)">
        <component :is="DeltaIcon" :size="14" aria-hidden="true" />
        {{ delta > 0 ? '+' : '' }}{{ delta }}%
      </span>
      <span v-if="deltaLabel || hint" class="text-text-muted">
        {{ deltaLabel || hint }}
      </span>
    </div>
  </div>
</template>
